import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from '@digital-twins/design-system/icons'

import i18n from '@/i18n'
import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { roundHalfAwayFromZero } from '@digital-twins/deck-ui/foundations/producer-rounding'
import { formatKpiFigure, KpiPlot, kpiRowsAdapter } from '@digital-twins/deck-ui/plots/kpi'

import { formatAbsoluteScore, formatKpiNumber, formatSharePercent } from './formatAbsoluteScore'
import { MetricFigure, MetricFigureSource, metricIsLaneMergedId, resolveMetricFigure } from './resolveMetricFigure'

import {
  CanonicalAnalysisOutput,
  isRatingUnitId,
  KpiListPayload,
  KpiListRow,
  kpiStatisticIdentity,
  ratingBounds,
} from '@digital-twins/shared-types'
import { KpiItem } from '@digital-twins/slide-schemas/generated/plot-types'

export interface KpiListViewProps {
  payload: KpiListPayload
  canonicalAnalysis?: CanonicalAnalysisOutput | null
  survey?: unknown
}

interface KpiInfoButtonProps {
  index: number
  questionText?: string | null
  frameLine: string
}

// The ring is existing report UI and it stays: a chart restating the figure it
// draws is a chart, not redundancy, and magnitude is what makes the column
// scannable. What it is ALLOWED to mean is narrower than before — see
// `ringsAShare`.
const RING_RADIUS = 22
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS
const RING_STROKE = 5
const RING_SIZE = (RING_RADIUS + RING_STROKE) * 2

/**
 * Does this row's figure fill a ring honestly?
 *
 * Only a share OF THE RESPONDENT BASE does. A mean, a median, a count, a delta
 * or an index has no 0-100 track to fill, and drawing one against 100 invents a
 * denominator the read does not have. `valueType === 'percent'` alone is not
 * enough: a percent-typed figure that is not one of these statistics gets the
 * plain number.
 */
const SHARE_STATISTICS = new Set<string>([
  'favorable_share',
  'top_two_share',
  // Top N (decision 20): the share of twins at the favourable end of the scale.
  'top_two_box',
  'top_box',
  'modal_share',
  'top_option_share',
])

function ringsAShare(row: KpiListRow): boolean {
  const identity = kpiStatisticIdentity(row.statistic)
  return row.value != null && row.valueType === 'percent' && identity != null && SHARE_STATISTICS.has(identity)
}

function CircularRing(props: { value: number; id: string; label: string }) {
  const { value, id, label } = props
  const pct = Math.min(Math.max(value, 0), 100)
  const filled = (pct / 100) * RING_CIRCUMFERENCE
  const gap = RING_CIRCUMFERENCE - filled
  const gradId = `ring-grad-${id}`

  return (
    <svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      className="flex-shrink-0"
      data-testid={`summary-kpi-ring-${id}`}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--report-accent-mid))" />
          <stop offset="100%" stopColor="hsl(var(--report-accent-light))" />
        </linearGradient>
      </defs>
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke="hsl(var(--border))"
        strokeWidth={RING_STROKE}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${gap}`}
        strokeDashoffset={RING_CIRCUMFERENCE / 4}
      />
      <text
        x={RING_SIZE / 2}
        y={RING_SIZE / 2 + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="700"
        fill="hsl(var(--foreground))"
      >
        {/*
          Rounded by the producer's rule, not JavaScript's. `kpi_rows._rating_row`
          stamps the share rounded half away from zero (`figure_round`), so
          `Math.round` here printed 63% on the ring while the exec strip and the
          rating slide printed 62% for the same 62.5. This is the rounding RULE
          only: the ring still shows a whole number, exactly as it did.
        */}
        {`${roundHalfAwayFromZero(pct)}%`}
      </text>
    </svg>
  )
}

function formatKpiTypedValue(
  row: KpiListRow,
  value: number | null | undefined,
): string | null {
  if (row.valueType === 'label' || value == null) {
    return null
  }
  if (row.valueType === 'percent') {
    return formatSharePercent(value)
  }
  if (row.valueType === 'currency' && row.unit) {
    return `${formatKpiNumber(value)} ${row.unit}`
  }
  if (row.valueType === 'delta') {
    const sign = value > 0 ? '+' : ''
    return `${sign}${formatKpiNumber(value)}`
  }
  if (row.valueType === 'number') {
    return formatKpiNumber(value)
  }
  // A mean is meaningless without the top of its scale: "3.8" could be out of
  // five or out of ten. Only a mean gets this treatment -- a derived figure
  // like the opportunity score is not on the scale its inputs were measured
  // on, so anchoring it there would overstate what it is.
  if (row.valueType === 'mean' && row.unit) {
    return formatAbsoluteScore(value, row.unit)
  }
  return formatKpiNumber(value)
}

function legacyFormatKpiFigure(row: KpiListRow): string | null {
  return formatKpiTypedValue(row, row.value)
}

function rowHasPerAssetCuts(row: KpiListRow): boolean {
  return Boolean(row.byAssetId && Object.keys(row.byAssetId).length > 0)
}

function kpiAssetColumns(
  payload: KpiListPayload,
  row: KpiListRow,
): Array<{ id: string; label: string }> {
  const options = payload.assetOptions ?? []
  if (options.length >= 2) {
    return options
  }
  const byAsset = row.byAssetId ?? {}
  return Object.keys(byAsset).map((id) => ({ id, label: id }))
}

/**
 * The verb that joins a figure to its cut.
 *
 * A cut label is a noun phrase — "4-5", "Control Tower", "median" — so without
 * a verb the row renders "47% Control Tower", which is not a sentence. The verb
 * is a property of the STATISTIC, not of the study, so it lives here rather
 * than being baked into each producer's `cutLabel`. Translated (D2): the verb
 * is the one word here a reader in another locale would notice is English.
 */
function cutVerb(statistic: KpiListRow['statistic']): string {
  return cutVerbFor(kpiStatisticIdentity(statistic))
}

/** `cutVerb` by statistic identity: the stamped plot names it (`KpiItem.statistic`). */
function cutVerbFor(identity: string | null | undefined): string {
  switch (identity) {
    case 'favorable_share':
      return i18n.t('studyReport.cutVerb.favorable_share', { ns: 'app' })
    case 'top_two_share':
      return i18n.t('studyReport.cutVerb.top_two_share', { ns: 'app' })
    case 'top_two_box':
      return i18n.t('studyReport.cutVerb.top_two_box', { ns: 'app' })
    case 'modal_share':
      return i18n.t('studyReport.cutVerb.modal_share', { ns: 'app' })
    case 'top_option_share':
      return i18n.t('studyReport.cutVerb.top_option_share', { ns: 'app' })
    case 'rank_index':
      return i18n.t('studyReport.cutVerb.rank_index', { ns: 'app' })
    case 'opportunity':
      return ''
    default:
      return ''
  }
}

/**
 * A cut is an OPTION LABEL (a modal choice, a top pick, a ranked item's own
 * title) for these three statistics, so it reads as quoted content — the
 * QuotesView convention (`&ldquo;…&rdquo;`). A favorable share's cut is a
 * scale band ("4-5") and a top-two share's names the author's own coded points,
 * never a respondent's own words, so neither quotes.
 */
const QUOTED_CUT_STATISTICS = new Set<string>([
  'modal_share',
  'top_option_share',
  'rank_index',
])

function wrapCutForDisplay(statistic: string | null | undefined, cut: string): string {
  return statistic != null && QUOTED_CUT_STATISTICS.has(statistic) ? `“${cut}”` : cut
}

/**
 * "4-5" -> "4 or 5" (an adjacent two-point cut); "8-10" -> "8 to 10" (a wider
 * cut). Anything else (an option label, free text) passes through unchanged —
 * only a plain numeric range is a scale cut worth re-wording. Both joiners are
 * translated (`kpiCutAdjacent` / `kpiCutRange`) rather than hardcoded English:
 * the adjacent-versus-wider distinction is deliberate and must survive
 * translation.
 */
function scaleCutRangeLabel(cut: string): string {
  const match = cut.match(/^(\d+)\s*-\s*(\d+)$/)
  if (!match) {
    return cut
  }
  const low = Number(match[1])
  const high = Number(match[2])
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) {
    return cut
  }
  return high - low === 1
    ? i18n.t('studyReport.kpiCutAdjacent', { low, high, ns: 'app' })
    : i18n.t('studyReport.kpiCutRange', { low, high, ns: 'app' })
}

/** The rating unit's top value ("rating_1_5" -> 5), or null off any other unit. */
function scaleTopFromUnit(unit: string | undefined): number | null {
  if (!unit || !isRatingUnitId(unit)) {
    return null
  }
  const [, max] = ratingBounds(unit)
  return Number.isFinite(max) ? max : null
}

/** The favorable end's own wording, picked by polarity — never the value's own end. */
function favorableAnchorLabel(figure: MetricFigure): string | null {
  const anchors = figure.scaleAnchors
  if (!anchors) {
    return null
  }
  const label = figure.higherIsBetter === false ? anchors.minLabel : anchors.maxLabel
  const trimmed = label?.trim()
  return trimmed || null
}

/**
 * "52% rated it 4 or 5 out of 5 (Strongly agree)" in English — only for a
 * favourable-share row whose figure carries BOTH a rating scale top and real
 * anchor wording. Without either, `fallbackFigureLine` degrades to today's
 * plain sentence: inventing "out of 5" off a unit with no scale, or a
 * parenthetical off generic wording, would state something the read does not
 * have.
 *
 * The whole sentence is ONE interpolated template per locale
 * (`kpiScaleSentence`), not fragments concatenated with English glue ("it",
 * "out of"). Each language controls its own word order; `verb` is still
 * passed even to a locale whose template does not reference `{{verb}}`, so
 * the single `cutVerb` translation stays the source of truth.
 */
function scaleSentence(
  row: KpiListRow,
  figure: string,
  cut: string,
  resolved: MetricFigure,
): string | null {
  if (kpiStatisticIdentity(row.statistic) !== 'favorable_share') {
    return null
  }
  const scaleTop = scaleTopFromUnit(resolved.unit)
  if (scaleTop == null) {
    return null
  }
  const endLabel = favorableAnchorLabel(resolved)
  if (!endLabel) {
    return null
  }
  const verb = cutVerb(row.statistic)
  return i18n.t('studyReport.kpiScaleSentence', {
    figure,
    verb,
    cut: scaleCutRangeLabel(cut),
    scaleTop,
    anchor: endLabel,
    ns: 'app',
  })
}

/**
 * Labels joined the way the reader's locale lists things: "A, B and C"
 * (conjunction) or "A or B" (disjunction).
 */
function formatLabelList(labels: Array<string>, type: 'conjunction' | 'disjunction'): string {
  try {
    return new Intl.ListFormat(i18n.language || 'en', { style: 'long', type }).format(labels)
  } catch {
    return labels.join(', ')
  }
}

function quotedLabels(labels: Array<string> | null | undefined): Array<string> {
  return (labels ?? []).map((label) => label.trim()).filter(Boolean).map((label) => `“${label}”`)
}

/** Two or more options share the top value: a tie, stated as one (D2). */
function tiedCuts(row: KpiListRow): Array<string> {
  const tied = (row.tiedCutLabels ?? []).map((label) => label.trim()).filter(Boolean)
  return tied.length > 1 ? tied : []
}

function fallbackFigureLine(row: KpiListRow, figure: string | null, resolved: MetricFigure | null): string {
  const tied = tiedCuts(row)
  if (figure && tied.length > 0) {
    return i18n.t('studyReport.kpiTieSentence', {
      figure,
      verb: cutVerb(row.statistic),
      cuts: formatLabelList(
        tied.map((label) => wrapCutForDisplay(row.statistic, label)),
        'conjunction',
      ),
      ns: 'app',
    })
  }
  const cut = row.cutLabel?.trim()
  if (figure && cut) {
    const scaled = resolved ? scaleSentence(row, figure, cut, resolved) : null
    if (scaled) {
      return scaled
    }
    const verb = cutVerb(row.statistic)
    const displayCut = wrapCutForDisplay(row.statistic, cut)
    return verb ? `${figure} ${verb} ${displayCut}` : `${figure} ${displayCut}`
  }
  // Per-asset rows carry the cut without a pooled figure: the percents sit
  // in the columns. Keep the same verb + cut sentence, never invent a pooled %.
  if (!figure && cut) {
    const verb = cutVerb(row.statistic)
    const ranged = kpiStatisticIdentity(row.statistic) === 'favorable_share' ? scaleCutRangeLabel(cut) : cut
    const displayCut = wrapCutForDisplay(row.statistic, ranged)
    return verb ? `${verb} ${displayCut}` : displayCut
  }
  // No cut stated: the producer's own statement beats a bare figure, which
  // would say nothing about what it counts.
  const statement = row.statement?.trim()
  if (figure && statement) {
    return `${figure} · ${statement}`
  }
  return figure ?? statement ?? ''
}

/**
 * The row's sentence: its figure and what it counts, always (D2). A persisted
 * `predicate` from an older report is never rendered: it replaced the cut, so
 * the reader could not tell which answer the figure counted.
 */
function kpiSentence(row: KpiListRow, figure: string | null, resolved: MetricFigure | null): string {
  return fallbackFigureLine(row, figure, resolved) || row.statement?.trim() || row.label
}

/**
 * A select row's metric-level `nativeValue` is its largest option share with no
 * option named. The row already states that share WITH its option, so the
 * tooltip does not repeat it as an unlabelled "average".
 */
const UNLABELLED_NATIVE_STATISTICS = new Set<KpiListRow['statistic']>([
  'modal_share',
  'top_option_share',
])

function tooltipFrameLine(
  row: KpiListRow,
  figure: MetricFigure | null,
  questionText?: string | null,
): string {
  // The row's own measure label, never the module heading: a grid measure's
  // row names the measure ("Is visually appealing"), while `moduleTitle` names
  // the block it sits in ("Creative effectiveness core") and would caption
  // every measure of that block alike.
  const title = row.label.trim()
  const stem = (questionText || '').trim()
  const parts: Array<string> = []
  // Never repeat the stem printed directly above the rule.
  if (title && title.toLowerCase() !== stem.toLowerCase()) {
    parts.push(title)
  }
  if (figure?.nativeValue != null && figure.unit && !UNLABELLED_NATIVE_STATISTICS.has(row.statistic)) {
    parts.push(
      `${formatAbsoluteScore(figure.nativeValue, figure.unit)} ${i18n.t('studyReport.kpiAverageSuffix', { ns: 'app' })}`,
    )
  }
  if (figure && figure.baseSize > 0) {
    parts.push(`n=${figure.baseSize}`)
  }
  const note = (row.note || '').trim()
  if (note) {
    parts.push(note)
  }
  return parts.join(' · ')
}

function visibleKpiRows(rows: Array<KpiListRow>, source: MetricFigureSource): Array<KpiListRow> {
  return rows.filter((row) => {
    if (rowHasPerAssetCuts(row)) {
      return true
    }
    const metricId = row.metricId ?? row.derivedFromMetricIds?.[0]
    if (metricId) {
      if (metricIsLaneMergedId(source, metricId)) {
        return false
      }
      const resolved = resolveMetricFigure(source, metricId)
      // A label-only KPI carries its read in `statement`, not in a figure, so an
      // unresolved metric does not make it blank.
      if (resolved == null && legacyFormatKpiFigure(row) == null && !row.statement?.trim()) {
        return false
      }
      return true
    }
    return legacyFormatKpiFigure(row) != null || Boolean(row.statement?.trim() || row.label)
  })
}

function figureForRow(source: MetricFigureSource, row: KpiListRow): MetricFigure | null {
  if (row.metricId) {
    return resolveMetricFigure(source, row.metricId)
  }
  const derived = row.derivedFromMetricIds?.[0]
  if (derived) {
    return resolveMetricFigure(source, derived)
  }
  return null
}

/**
 * Catalog verbs stay lowercase so they can follow a figure ("75% chose …").
 * On screen the figure often moves onto the ring, which leaves the verb as
 * the first word of the row. That word is a sentence, so it takes a capital.
 */
function sentenceCaseWhenVerbLeads(
  text: string,
  statistic: KpiListRow['statistic'],
  alwaysLeads = false,
): string {
  return sentenceCaseWhenVerbLeadsFor(text, kpiStatisticIdentity(statistic), alwaysLeads)
}

function sentenceCaseWhenVerbLeadsFor(
  text: string,
  identity: string | null | undefined,
  alwaysLeads = false,
): string {
  const trimmed = text.trim()
  const verb = cutVerbFor(identity).trim()
  if (!trimmed || (!verb && !alwaysLeads)) {
    return trimmed
  }
  const leads = alwaysLeads || trimmed === verb || trimmed.startsWith(`${verb} `)
  if (!leads) {
    return trimmed
  }
  return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1)
}

/**
 * A per-asset row names the option that leads in the most versions. When
 * several lead equally often it says so, in the question's option order (N4);
 * the columns are the first one's shares, the one the sentence names.
 */
function tiedLeadLine(row: KpiListRow): string | null {
  if (row.value != null || !rowHasPerAssetCuts(row)) {
    return null
  }
  const labels = quotedLabels(row.tiedCutLabels)
  if (labels.length < 2) {
    return null
  }
  return i18n.t('studyReport.kpiTiedLeadLine', {
    answers: formatLabelList(labels, 'conjunction'),
    ns: 'app',
  })
}

/** "12% chose “None of these” or “Cannot recall”", when the row has one (D2). */
function exclusiveLine(row: KpiListRow): string | null {
  const labels = quotedLabels(row.exclusiveLabels)
  if (row.exclusiveShare == null || row.exclusiveShare <= 0 || labels.length === 0) {
    return null
  }
  return i18n.t('studyReport.kpiExclusiveLine', {
    share: formatSharePercent(row.exclusiveShare),
    answers: formatLabelList(labels, 'disjunction'),
    ns: 'app',
  })
}

/**
 * "Asked of 20 people who bought a branded salad" when PLAN named the cohort
 * (D3); otherwise "Asked of the 20 who answered “Yes” or “No” to “…”" (D2).
 */
function cohortLine(row: KpiListRow, base: number | null): string | null {
  const cohort = row.cohort
  const authored = cohort?.label?.trim()
  if (cohort && base != null && authored) {
    return i18n.t('studyReport.kpiCohortLabelLine', { n: base, cohort: authored, ns: 'app' })
  }
  const labels = quotedLabels(cohort?.answerLabels)
  if (!cohort || base == null || labels.length === 0 || !cohort.questionText?.trim()) {
    return null
  }
  return i18n.t('studyReport.kpiCohortLine', {
    n: base,
    answers: formatLabelList(labels, 'disjunction'),
    question: cohort.questionText.trim(),
    ns: 'app',
  })
}

/** One decimal on every share, split so the point lines up down the column. */
function KpiAssetValue(props: { row: KpiListRow; value: number | null | undefined }) {
  const { row, value } = props

  return (
    <span className="block text-right text-sm font-semibold tabular-nums leading-snug text-foreground">
      {formatKpiTypedValue(row, value) ?? '—'}
    </span>
  )
}

function KpiValueSlot(props: { row: KpiListRow; index: number; figure: string }) {
  const { row, index, figure } = props
  const isDelta = row.valueType === 'delta'
  const deltaColor =
    row.value != null && row.value < 0
      ? 'text-[hsl(var(--chart-negative))]'
      : 'text-[hsl(var(--chart-positive))]'

  return (
    <span
      data-testid={`summary-kpi-number-${index}`}
      className={cn('tabular-nums font-semibold', isDelta ? deltaColor : undefined)}
    >
      {figure}
    </span>
  )
}

function splitSentenceAroundFigure(sentence: string, figure: string | null): {
  before: string
  figure: string | null
  after: string
} {
  if (!figure) {
    return { before: sentence, figure: null, after: '' }
  }
  const at = sentence.indexOf(figure)
  if (at < 0) {
    return { before: sentence, figure: null, after: '' }
  }
  return {
    before: sentence.slice(0, at),
    figure,
    after: sentence.slice(at + figure.length),
  }
}

function KpiInfoButton(props: KpiInfoButtonProps) {
  const { index, questionText, frameLine } = props
  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
  }

  function handlePointerDown(event: { preventDefault: () => void }) {
    event.preventDefault()
  }

  function handleClick(event: { preventDefault: () => void }) {
    event.preventDefault()
    setOpen((current) => !current)
  }

  const stem = questionText?.trim() || ''
  const hasRule = Boolean(stem && frameLine)

  return (
    <Tooltip open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={`summary-kpi-info-${index}`}
          aria-label={t('studyReport.kpiFigureInfo')}
          aria-expanded={open}
          onPointerDown={handlePointerDown}
          onClick={handleClick}
          className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] space-y-2 p-3 text-left">
        {stem ? (
          <Text size="sm" data-testid={`summary-kpi-info-question-${index}`} className="leading-snug">
            {stem}
          </Text>
        ) : null}
        {hasRule ? <div className="border-t border-border/60" /> : null}
        {frameLine ? (
          <Text
            size="sm"
            color="muted"
            data-testid={`summary-kpi-info-frame-${index}`}
            className="leading-snug"
          >
            {frameLine}
          </Text>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}

// -- the stamped read (plots plan F) ------------------------------------------------
//
// A card Python stamped with its `kpi` plot draws through the plot's row
// layout: every figure, base, format and per-asset cell is the plot's, printed
// by its one formatter. The card composes each row's words in the viewer's
// language from the typed parts Python stated (`KpiItem.detail`), with the
// same templates the legacy read uses below; nothing is read off
// `metricResults`. The legacy read stays for cards stored before the stamp.

function stampedTiedCuts(item: KpiItem): Array<string> {
  const tied = (item.detail?.tiedCuts ?? []).map((label) => label.trim()).filter(Boolean)
  return tied.length > 1 ? tied : []
}

/** The legacy `fallbackFigureLine`, on the stamped item's typed parts. */
function stampedSentence(item: KpiItem, figure: string | null): string {
  const identity = item.statistic ?? null
  const detail = item.detail
  const verb = cutVerbFor(identity)
  const tied = stampedTiedCuts(item)
  if (figure && tied.length > 0) {
    return i18n.t('studyReport.kpiTieSentence', {
      figure,
      verb,
      cuts: formatLabelList(
        tied.map((label) => wrapCutForDisplay(identity, label)),
        'conjunction',
      ),
      ns: 'app',
    })
  }
  const cut = detail?.cut?.trim()
  if (figure && cut) {
    const scale = detail?.scale
    if (identity === 'favorable_share' && scale) {
      return i18n.t('studyReport.kpiScaleSentence', {
        figure,
        verb,
        cut: scaleCutRangeLabel(cut),
        scaleTop: scale.top,
        anchor: scale.anchor,
        ns: 'app',
      })
    }
    const displayCut = wrapCutForDisplay(identity, cut)
    return verb ? `${figure} ${verb} ${displayCut}` : `${figure} ${displayCut}`
  }
  if (!figure && cut) {
    const ranged = identity === 'favorable_share' ? scaleCutRangeLabel(cut) : cut
    const displayCut = wrapCutForDisplay(identity, ranged)
    return verb ? `${verb} ${displayCut}` : displayCut
  }
  const statement = detail?.statement?.trim()
  if (figure && statement) {
    return `${figure} · ${statement}`
  }
  // The Average tab (decision 20): a mean says what it is.
  if (figure && identity === 'mean' && item.views) {
    return i18n.t('studyReport.kpiAverageSentence', { figure, ns: 'app' })
  }
  return figure ?? statement ?? detail?.measure ?? item.label
}

function stampedFrameLine(item: KpiItem): string {
  const detail = item.detail
  const title = (detail?.measure ?? '').trim()
  const stem = (item.questionText ?? '').trim()
  const parts: Array<string> = []
  if (title && title.toLowerCase() !== stem.toLowerCase()) {
    parts.push(title)
  }
  const average = detail?.average ? formatKpiFigure(detail.average) : null
  if (average) {
    parts.push(`${average} ${i18n.t('studyReport.kpiAverageSuffix', { ns: 'app' })}`)
  }
  if (item.base != null && item.base > 0) {
    parts.push(`n=${item.base}`)
  }
  const note = (detail?.note ?? '').trim()
  if (note) {
    parts.push(note)
  }
  return parts.join(' · ')
}

function stampedNotes(item: KpiItem): Array<{ key: string; text: string; small?: boolean }> {
  const detail = item.detail
  const notes: Array<{ key: string; text: string; small?: boolean }> = []
  const exclusive = detail?.exclusive
  const exclusiveShare = exclusive ? formatKpiFigure(exclusive.figure) : null
  const exclusiveLabels = quotedLabels(exclusive?.labels)
  if (exclusiveShare && exclusiveLabels.length > 0) {
    notes.push({
      key: 'exclusive',
      text: i18n.t('studyReport.kpiExclusiveLine', {
        share: exclusiveShare,
        answers: formatLabelList(exclusiveLabels, 'disjunction'),
        ns: 'app',
      }),
    })
  }
  const leads = quotedLabels(detail?.tiedLeads)
  if (leads.length > 1) {
    notes.push({
      key: 'tied-lead',
      text: i18n.t('studyReport.kpiTiedLeadLine', {
        answers: formatLabelList(leads, 'conjunction'),
        ns: 'app',
      }),
    })
  }
  const cohort = detail?.cohort
  const base = item.base ?? null
  const authored = cohort?.label?.trim()
  if (cohort && base != null && authored) {
    notes.push({
      key: 'cohort',
      small: true,
      text: i18n.t('studyReport.kpiCohortLabelLine', { n: base, cohort: authored, ns: 'app' }),
    })
  } else {
    const answers = quotedLabels(cohort?.answerLabels)
    const question = cohort?.questionText?.trim()
    if (cohort && base != null && answers.length > 0 && question) {
      notes.push({
        key: 'cohort',
        small: true,
        text: i18n.t('studyReport.kpiCohortLine', {
          n: base,
          answers: formatLabelList(answers, 'disjunction'),
          question,
          ns: 'app',
        }),
      })
    }
  }
  return notes
}

function stampedRingsAShare(item: KpiItem): boolean {
  return (
    item.figure.format === 'percent' &&
    item.figure.value != null &&
    item.statistic != null &&
    SHARE_STATISTICS.has(item.statistic) &&
    !(item.byLane && item.byLane.length > 0)
  )
}

interface StampedKpiRowsProps {
  plot: ReturnType<typeof kpiRowsAdapter.read>
}

function StampedKpiRows(props: StampedKpiRowsProps) {
  const { plot } = props
  const { t } = useTranslation('app')

  function rowText(item: KpiItem, index: number, figure: string | null) {
    const sentence = stampedSentence(item, figure)
    const parts = splitSentenceAroundFigure(sentence, figure)
    // The ring carries the figure, so the sentence must not repeat it, but
    // only when the figure LEADS the sentence (see the legacy read).
    const figureLeads = stampedRingsAShare(item) && parts.figure != null && parts.before.trim() === ''
    const tie = stampedTiedCuts(item).length > 0
    const studyBase = item.detail?.studyBase ?? null
    const base =
      item.base != null
        ? studyBase != null
          ? t('studyReport.kpiBaseOfStudy', { n: item.base, total: studyBase })
          : t('studyReport.kpiBase', { n: item.base })
        : null
    const body = figureLeads ? (
      sentenceCaseWhenVerbLeadsFor(parts.after, item.statistic, tie)
    ) : parts.figure ? (
      <>
        {parts.before}
        <span data-testid={`summary-kpi-number-${index}`} className="tabular-nums font-semibold">
          {parts.figure}
        </span>
        {parts.after}
      </>
    ) : (
      sentenceCaseWhenVerbLeadsFor(parts.before, item.statistic)
    )
    return { base, sentence: body, notes: stampedNotes(item) }
  }

  function renderRowAside(item: KpiItem, index: number, figure: string | null) {
    if (!stampedRingsAShare(item) || item.figure.value == null) {
      return null
    }
    return <CircularRing value={item.figure.value} id={String(index)} label={figure ?? ''} />
  }

  function renderRowAction(item: KpiItem, index: number) {
    return <KpiInfoButton index={index} questionText={item.questionText} frameLine={stampedFrameLine(item)} />
  }

  return (
    <div className="flex flex-col gap-4" data-testid="summary-kpi-list">
      <KpiPlot
        data={plot.data}
        props={{ ...(plot.props ?? {}), layout: 'row' }}
        labelOverrides={{
          plot_not_available: t('studyReport.plotNotAvailable'),
          plot_nps_score: t('studyReport.plotNpsScore'),
          stat_tab_average: t('studyReport.statTabAverage'),
          stat_tab_top_n: t('studyReport.statTabTopN', { n: '{n}' }),
          stat_tab_nps: t('studyReport.statTabNps'),
        }}
        testIdPrefix="summary-kpi"
        rowText={rowText}
        renderRowAside={renderRowAside}
        renderRowAction={renderRowAction}
      />
    </div>
  )
}

export function KpiListView(props: KpiListViewProps) {
  const { payload } = props

  if (kpiRowsAdapter.accepts(payload)) {
    return (
      <TooltipProvider delayDuration={150}>
        <StampedKpiRows plot={kpiRowsAdapter.read(payload)} />
      </TooltipProvider>
    )
  }
  return <LegacyKpiListView {...props} />
}

/**
 * The legacy reader, for cards stored before Python stamped the plot: reads
 * each row's figure off `metricResults` (`resolveMetricFigure`) and prints it
 * with `formatAbsoluteScore`. Unchanged; deleted with the last stored card.
 */
function LegacyKpiListView(props: KpiListViewProps) {
  const { payload, canonicalAnalysis, survey } = props
  const { t } = useTranslation('app')
  const source: MetricFigureSource = canonicalAnalysis
    ? { ...canonicalAnalysis, survey }
    : { survey }
  const rows = visibleKpiRows(payload.rows, source)

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-col gap-4" data-testid="summary-kpi-list">
        <div className="flex flex-col">
          {rows.map((row, index) => {
            const figure = legacyFormatKpiFigure(row)
            const resolved = figureForRow(source, row)
            const sentence = kpiSentence(row, figure, resolved)
            const parts = splitSentenceAroundFigure(sentence, figure)
            // The ring carries the figure, so the sentence must not repeat it —
            // but only when the figure LEADS the sentence; stripping it from
            // mid-clause would cut a hole in the middle of a sentence.
            const showRing = ringsAShare(row) && !rowHasPerAssetCuts(row)
            const figureLeads = showRing && parts.figure != null && parts.before.trim() === ''
            const questionText = resolved?.questionText ?? row.questionText
            const frameLine = tooltipFrameLine(row, resolved, questionText)
            const assetColumns = rowHasPerAssetCuts(row) ? kpiAssetColumns(payload, row) : []
            // Each row can answer a different question for a different set of
            // respondents. Keep the typed question text and its own resolved
            // base beside that row instead of collapsing them into one card
            // footer. This is a direct render of persisted provenance, not a
            // rewritten interpretation of Yes/No, negation, or uncertainty.
            // PLAN's authored name for the read when it has one (D3); the full
            // question stays in the tooltip.
            const context = row.reportLabel?.trim() || questionText?.trim() || row.label
            // The row's own typed base (D2) when the producer stamped one; the
            // metric's resolved base for reports written before it.
            const baseSize =
              row.validAnswerBase != null && row.validAnswerBase > 0
                ? row.validAnswerBase
                : resolved?.baseSize && resolved.baseSize > 0
                  ? resolved.baseSize
                  : null
            const studyBase =
              baseSize != null && row.studyBase != null && row.studyBase > baseSize
                ? row.studyBase
                : null
            const exclusive = exclusiveLine(row)
            const tiedLead = tiedLeadLine(row)
            const cohort = cohortLine(row, baseSize)
            const tie = tiedCuts(row).length > 0

            return (
              <div
                key={row.metricId ?? `${row.label}-${index}`}
                data-testid={`summary-kpi-row-${index}`}
                data-kpi-display={row.valueType}
              >
                {index > 0 && <div className="border-t border-border/50 my-3" />}
                <div className="flex items-start gap-3 min-w-0">
                  {showRing && row.value != null ? (
                    <CircularRing
                      value={row.value}
                      id={String(index)}
                      label={figure ?? ''}
                    />
                  ) : null}
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    {/* The context takes the text column's full width. The base is
                        its own line under it, never a column beside it: in the
                        narrow Insights card a side column squeezed a long question
                        into a strip of two or three words per line. */}
                    <Text
                      as="span"
                      size="sm"
                      data-testid={`summary-kpi-context-${index}`}
                      className="min-w-0 leading-snug text-muted-foreground"
                    >
                      {context}
                    </Text>
                    {baseSize != null ? (
                      <Text
                        as="span"
                        size="sm"
                        data-testid={`summary-kpi-base-${index}`}
                        className="text-[10px] tabular-nums text-muted-foreground"
                      >
                        {studyBase != null
                          ? t('studyReport.kpiBaseOfStudy', { n: baseSize, total: studyBase })
                          : t('studyReport.kpiBase', { n: baseSize })}
                      </Text>
                    ) : null}
                    <div
                      className={cn(
                        'min-w-0',
                        assetColumns.length > 0
                          ? 'grid grid-cols-[minmax(0,1fr)_5rem] items-start gap-x-3 gap-y-1.5'
                          : 'flex items-start gap-1.5',
                      )}
                    >
                      <Text
                        as="span"
                        size="sm"
                        data-testid={`summary-kpi-sentence-${index}`}
                        className="min-w-0 font-semibold leading-snug text-foreground"
                      >
                        {figureLeads ? (
                          sentenceCaseWhenVerbLeads(parts.after, row.statistic, tie)
                        ) : parts.figure ? (
                          <>
                            {parts.before}
                            <KpiValueSlot row={row} index={index} figure={parts.figure} />
                            {parts.after}
                          </>
                        ) : (
                          sentenceCaseWhenVerbLeads(parts.before, row.statistic)
                        )}
                      </Text>
                      <div className="flex justify-end">
                      <KpiInfoButton
                        index={index}
                        questionText={questionText}
                        frameLine={frameLine}
                      />
                    </div>
                    {assetColumns.length > 0 ? (
                      <div data-testid={`summary-kpi-assets-${index}`} className="contents">
                        {assetColumns.map((option) => {
                          const raw = row.byAssetId?.[option.id]

                          return (
                            <div
                              key={option.id}
                              data-testid={`summary-kpi-cell-${index}-${option.id}`}
                              className="contents"
                            >
                              <Text as="span" size="sm" color="muted" className="min-w-0 leading-snug">
                                {option.label}
                              </Text>
                              <KpiAssetValue row={row} value={raw} />
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                    </div>
                    {exclusive ? (
                      <Text
                        as="span"
                        size="sm"
                        color="muted"
                        data-testid={`summary-kpi-exclusive-${index}`}
                        className="min-w-0 leading-snug"
                      >
                        {exclusive}
                      </Text>
                    ) : null}
                    {tiedLead ? (
                      <Text
                        as="span"
                        size="sm"
                        color="muted"
                        data-testid={`summary-kpi-tied-lead-${index}`}
                        className="min-w-0 leading-snug"
                      >
                        {tiedLead}
                      </Text>
                    ) : null}
                    {cohort ? (
                      <Text
                        as="span"
                        size="sm"
                        color="muted"
                        data-testid={`summary-kpi-cohort-${index}`}
                        className="min-w-0 text-[11px] leading-snug"
                      >
                        {cohort}
                      </Text>
                    ) : null}
                  </div>
                </div>
                <div
                  data-testid="summary-kpi-row-segment"
                  className="min-h-4"
                  aria-hidden="true"
                />
              </div>
            )
          })}
        </div>
      </div>
    </TooltipProvider>
  )
}
